import { CONFIG } from 'src/environments/environment';
import { SITE_ID, SiteIdEnum } from 'src/config-global';

import type { OfflineDemoAvailability } from './types';

export const OFFLINE_DEMO_FEATURE_ENABLED = true;

type EnvironmentPolicyInput = Pick<
  typeof CONFIG,
  | 'environment'
  | 'VITE_CURRENT_ENV'
  | 'VITE_APP_DOMAINS'
  | 'VITE_HOST_API'
  | 'VITE_PUBLIC_API'
  | 'VITE_ATH_API'
  | 'VITE_ATH_API_PUBLIC'
  | 'VITE_FIREBASE_PROJECT_ID'
  | 'VITE_FIREBASE_AUTH_DOMAIN'
  | 'VITE_FIREBASE_STORAGE_BUCKET'
  | 'VITE_FIREBASE_REALTIME_DB_URL'
  | 'VITE_STRIPE_PUBLISHABLE_KEY'
>;

const KNOWN_PRODUCTION_HOSTS = [
  'api-v4.pncf.app',
  'dashboard.point-craft.com',
  'app.pointcraft.io',
];

function hasProductionLikeConfiguration(config: EnvironmentPolicyInput): boolean {
  if (config.VITE_STRIPE_PUBLISHABLE_KEY.trim().toLowerCase().startsWith('pk_live_')) return true;

  const configuredValues = [
    config.VITE_HOST_API,
    config.VITE_PUBLIC_API,
    config.VITE_ATH_API,
    config.VITE_ATH_API_PUBLIC,
    config.VITE_FIREBASE_PROJECT_ID,
    config.VITE_FIREBASE_AUTH_DOMAIN,
    config.VITE_FIREBASE_STORAGE_BUCKET,
    config.VITE_FIREBASE_REALTIME_DB_URL,
    ...Object.values(config.VITE_APP_DOMAINS),
  ].map((value) => value.trim().toLowerCase());

  return configuredValues.some(
    (value) =>
      /(^|[-._/])prod(?:uction)?($|[-._/])/.test(value) ||
      KNOWN_PRODUCTION_HOSTS.some((host) => value.includes(host))
  );
}

export function evaluateOfflineDemoAvailability(
  config: EnvironmentPolicyInput,
  siteId: SiteIdEnum | string,
  featureEnabled = OFFLINE_DEMO_FEATURE_ENABLED
): OfflineDemoAvailability {
  if (!featureEnabled) return { available: false, reason: 'globally-disabled' };
  if (siteId !== SiteIdEnum.PointOfSale) {
    return { available: false, reason: 'unsupported-site' };
  }

  const environment = config.environment.trim().toLowerCase();
  const { isLocal, isDev, isStaging, isMock, isProd } = config.VITE_CURRENT_ENV;

  if (isProd) return { available: false, reason: 'production' };
  if (!environment || environment.includes('prod')) {
    return { available: false, reason: 'production-like-name' };
  }
  if (hasProductionLikeConfiguration(config)) {
    return { available: false, reason: 'production-like-config' };
  }

  const activeNonProductionFlags = [isLocal, isDev, isStaging, isMock].filter(Boolean).length;
  if (activeNonProductionFlags !== 1) {
    return { available: false, reason: 'contradictory-flags' };
  }

  const environmentMatches =
    (environment === 'dev' && (isLocal || isDev)) ||
    (environment === 'staging' && isStaging) ||
    (environment === 'mock' && isMock) ||
    (environment === 'test' && isMock);

  if (!environmentMatches) {
    return { available: false, reason: 'unsupported-environment' };
  }

  return { available: true, reason: 'available' };
}

export const offlineDemoAvailability = evaluateOfflineDemoAvailability(CONFIG, SITE_ID);
